<script setup lang="ts">
import { computed, onMounted, ref, type FunctionalComponent } from 'vue';
import { useI18n } from 'vue-i18n';
import { fetchAbout, type AboutResponse } from '@/lib/about';
import { formatDate, formatDateTime } from '@/lib/datetime';

interface NavItem {
    name: string;
    to: { name: string };
    icon: FunctionalComponent;
    badge?: number | null;
    /** The count in words, e.g. "3 paid orders to ship". */
    badgeLabel?: string;
}

// A three-digit pill would crowd the label; past 99 the exact number
// matters less than "a lot".
function badgeText(n: number): string {
    return n > 99 ? '99+' : String(n);
}

interface NavSection {
    /** Group heading; null for the leading ungrouped section (Dashboard). */
    label: string | null;
    items: NavItem[];
}

defineProps<{
    nav: NavSection[];
}>();

const { t } = useI18n();

// Loaded once per SPA boot. Silent on failure — the footer just
// won't render. ROLE_USER endpoint, so editors get it too.
const about = ref<AboutResponse | null>(null);
onMounted(async () => {
    try {
        about.value = await fetchAbout();
    } catch {
        // Silent
    }
});

// The release the running api image was built from. One monorepo tag now
// covers core, api, public and worker, so this is the CMS version rather
// than a per-package one. Outside a CI-built image it reads `dev-main`
// (the path repository's constraint) and carries no build date.
const version = computed(() => about.value?.cms_core ?? null);
const builtOn = computed(() => about.value?.built_at ? formatDate(about.value.built_at) : null);
const versionTitle = computed(() => {
    const v = version.value;
    if (v === null) return '';
    const at = about.value?.built_at;
    return at ? t('shell.sidebar.versionTitle', { version: v, date: formatDateTime(at) }) : `CMS ${v}`;
});
</script>

<template>
    <div class="flex h-full flex-col">
        <div class="flex h-20 shrink-0 items-center">
            <!-- Light/dark logo swap via CSS only — the `.dark` class on
                 <html> (theme store) flips which one renders. -->
            <img src="/images/logo.png" alt="CEEMS" class="h-16 w-auto dark:hidden" />
            <img src="/images/logo-dark.png" alt="CEEMS" class="hidden h-16 w-auto dark:block" />
        </div>
        <nav class="flex flex-1 flex-col">
            <div class="flex flex-1 flex-col gap-y-1">
                <template v-for="(section, i) in nav" :key="section.label ?? `__${i}`">
                    <!-- Section heading; the leading ungrouped block (label
                         null) gets no header and no top border. -->
                    <p v-if="section.label"
                       class="mt-4 px-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500"
                       :class="i > 0 && 'border-t border-slate-100 pt-4 dark:border-slate-700'">
                        {{ section.label }}
                    </p>
                    <ul role="list" class="flex flex-col gap-y-1">
                        <li v-for="item in section.items" :key="item.name">
                            <RouterLink
                                :to="item.to"
                                class="group relative flex gap-x-3 rounded-md px-2 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-700"
                                active-class="bg-blue-50 text-blue-700 before:absolute before:inset-y-1.5 before:-left-px before:w-0.5 before:rounded-full before:bg-blue-600 dark:bg-blue-500/12 dark:text-blue-300 dark:before:bg-blue-400">
                                <component :is="item.icon" class="h-5 w-5 shrink-0" />
                                {{ item.name }}
                                <span v-if="item.badge"
                                      class="ml-auto min-w-5 self-center rounded-full bg-blue-600 px-1.5 py-0.5 text-center text-[11px] leading-none font-semibold text-white tabular-nums dark:bg-blue-500"
                                      :title="item.badgeLabel ?? String(item.badge)">
                                    <span :aria-hidden="item.badgeLabel ? 'true' : undefined">{{ badgeText(item.badge) }}</span><span v-if="item.badgeLabel" class="sr-only">{{ item.badgeLabel }}</span>
                                </span>
                            </RouterLink>
                        </li>
                    </ul>
                </template>
            </div>
            <p v-if="version"
               class="mt-3 text-[10px] leading-snug text-slate-400 dark:text-slate-500"
               :title="versionTitle">
                CMS {{ version }}<template v-if="builtOn"> · {{ builtOn }}</template>
            </p>
            <i18n-t keypath="shell.madeBy" tag="p" scope="global"
                    class="text-[10px] leading-snug text-slate-400 dark:text-slate-500"
                    :class="!version && 'mt-3'">
                <template #gsid>
                    <a href="https://gsid.nl" target="_blank" rel="noopener" class="font-medium underline-offset-2 hover:text-slate-600 hover:underline dark:hover:text-slate-300">GSID</a>
                </template>
            </i18n-t>
        </nav>
    </div>
</template>
